import { useCallback, useMemo, useState } from 'react';
import { useMutation } from 'data-fetching-client';
import { useSelector } from 'react-redux';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import { GREAT_WHITE } from 'HubStyleTokens/colors';
import UISection from 'UIComponents/section/UISection';
import Rating from 'marketplace-ui-reviews/components/Rating';
import { Reportables } from 'marketplace-ui-reviews/constants/ReportReasons';
import { RatingAxes } from 'marketplace-ui-reviews/constants/RatingAxes';
import HelpfulButton from 'marketplace-ui-reviews/components/HelpfulButton';
import ReviewActionsWrapper from 'marketplace-ui-reviews/components/ReviewActionsWrapper';
import ReportButton from 'marketplace-ui-reviews/components/ReportButton';
import ReviewBody from 'marketplace-ui-reviews/components/ReviewBody';
import ReviewerInfo from 'marketplace-ui-reviews/components/ReviewerInfo';
import ReviewReplyThread from 'marketplace-ui-reviews/components/ReviewReplyThread';
import ReportReviewModal from 'marketplace-ui-reviews/components/modals/ReportReviewModal';
import { REPORT_REVIEW } from 'marketplace-ui-reviews/queries/reportReviewQuery';
import { REPORT_REPLY } from 'marketplace-ui-reviews/queries/reportReplyQuery';
import { SET_REVIEW_HELPFUL } from 'marketplace-ui-reviews/queries/setReviewHelpfulQuery';
import { getUserMarkedHelpful } from 'marketplace-ui-reviews/utils/getUserMarkedHelpful';

// @ts-expect-error untyped dependency
import { getIsCustomer, getUserId } from '../../selectors/auth';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function AssetReviewRenderer({
  borderBottom,
  review,
  searchTerm,
  reviewType
}) {
  const [reportModalType, setReportModalType] = useState(null);
  const isCustomer = useSelector(getIsCustomer);
  const userId = useSelector(getUserId);
  const ratingValue = review.getIn(['ratings', RatingAxes.OVERALL]);
  const userMarkedHelpful = getUserMarkedHelpful(userId, review);
  const [localMarkedHelpful, setUserMarkedHelpful] = useState(userMarkedHelpful);
  const [setIsHelpful] = useMutation(SET_REVIEW_HELPFUL);
  const [reportReview] = useMutation(REPORT_REVIEW);
  const [reportReply] = useMutation(REPORT_REPLY);
  const handleClickHelpful = useCallback(() => {
    return new Promise((resolve, reject) => {
      setIsHelpful({
        variables: {
          reviewId: review.get('id'),
          isHelpful: !localMarkedHelpful
        }
      }).then(() => {
        setUserMarkedHelpful(!localMarkedHelpful);
        resolve();
      }).catch(err => {
        reject(err);
      });
    });
  }, [review, setIsHelpful, localMarkedHelpful]);
  const renderedReportModal = useMemo(() => {
    const onConfirmAction = (reportReason, comment) => reportModalType === Reportables.REVIEW ? reportReview({
      variables: {
        reviewId: review.get('id'),
        reportReason,
        comment
      }
    }) : reportReply({
      variables: {
        reviewId: review.get('id'),
        replyId: review.getIn(['reply', 'id']),
        reportReason,
        comment
      }
    });
    const handleReportModalConfirm = ({
      reason,
      comment
    }) => {
      return new Promise((resolve, reject) => {
        onConfirmAction(reason, comment).then(() => {
          resolve();
          setReportModalType(null);
        }).catch(err => {
          reject(err);
        });
      });
    };
    return /*#__PURE__*/_jsx(ReportReviewModal, {
      review: review,
      onClose: () => {
        setReportModalType(null);
      },
      onConfirm: handleReportModalConfirm
    });
  }, [reportModalType, reportReply, reportReview, review]);
  const helpfulCount = useMemo(() => {
    const currentHelpfulCount = review.getHelpfulCount();
    if (userMarkedHelpful && !localMarkedHelpful) {
      return currentHelpfulCount > 0 ? currentHelpfulCount - 1 : 0;
    }
    if (!userMarkedHelpful && localMarkedHelpful) {
      return currentHelpfulCount + 1;
    }
    return currentHelpfulCount;
  }, [localMarkedHelpful, review, userMarkedHelpful]);
  const reviewActions = useMemo(() => {
    return /*#__PURE__*/_jsxs(ReviewActionsWrapper, {
      children: [/*#__PURE__*/_jsx(HelpfulButton, {
        className: "m-right-2",
        disabled: !isCustomer,
        helpfulCount: helpfulCount,
        onClickHelpful: handleClickHelpful,
        userMarkedHelpful: localMarkedHelpful
      }), /*#__PURE__*/_jsx(ReportButton, {
        className: "m-left-2",
        isCustomer: isCustomer,
        onClick: () => setReportModalType(Reportables.REVIEW)
      })]
    });
  }, [handleClickHelpful, helpfulCount, isCustomer, localMarkedHelpful]);
  return /*#__PURE__*/_jsxs("div", {
    className: "p-y-8",
    style: {
      width: '100%',
      borderBottom: borderBottom ? `1px solid ${GREAT_WHITE}` : undefined
    },
    children: [/*#__PURE__*/_jsx(UISection, {
      children: /*#__PURE__*/_jsx(Rating, {
        direction: "column",
        label: /*#__PURE__*/_jsx(FormattedDateTime, {
          value: review.get('createdAt'),
          format: "ll",
          type: "userTz"
        }),
        rating: ratingValue || 0,
        showTextRating: true
      })
    }), /*#__PURE__*/_jsx(ReviewBody, {
      review: review,
      searchTerm: searchTerm,
      reviewType: reviewType
    }), /*#__PURE__*/_jsx(ReviewerInfo, {
      review: review,
      inline: true
    }), reviewActions, /*#__PURE__*/_jsx(ReviewReplyThread, {
      reply: review.get('reply'),
      onReportReplyClick: () => setReportModalType(Reportables.REPLY)
    }), reportModalType && renderedReportModal]
  });
}